import { isPlatformBrowser } from '@angular/common';
import { DestroyRef, Injectable, PLATFORM_ID, computed, effect, inject, signal } from '@angular/core';
import { TenantService } from '../../../core/tenant/tenant.service';
import type { AssignedCommentItem } from '../models/assigned-comments.models';
import { AssignedCommentsApiService } from '../services/assigned-comments-api.service';

@Injectable({ providedIn: 'root' })
export class ExternalRepliesStore {
  private readonly tenant = inject(TenantService);
  private readonly api = inject(AssignedCommentsApiService);
  private readonly dataSignal = signal<AssignedCommentItem[]>([]);
  private readonly errorSignal = signal(false);
  private readonly loadingSignal = signal(false);
  private generation = 0;
  private activeRequest: number | null = null;
  readonly data = this.dataSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly pendingCardIds = computed(() => new Set(this.data().map(item => item.cardId)));
  readonly count = computed(() => this.data().length);

  constructor() {
    effect(() => {
      const companyId = this.tenant.companyId();
      this.generation++;
      this.dataSignal.set([]);
      this.errorSignal.set(false);
      this.loadingSignal.set(false);
      if (companyId) void this.refresh();
    });
    if (isPlatformBrowser(inject(PLATFORM_ID))) {
      const timer = setInterval(() => void this.refresh(), 30_000);
      inject(DestroyRef).onDestroy(() => clearInterval(timer));
    }
  }

  async refresh(): Promise<void> {
    const companyId = this.tenant.companyId();
    const generation = this.generation;
    if (!companyId || this.activeRequest === generation) return;
    this.activeRequest = generation;
    this.loadingSignal.set(true);
    try {
      const items: AssignedCommentItem[] = [];
      let page = 1;
      let total = 0;
      do {
        const result = await this.api.getAssignedComments({
          page, pageSize: 100, readFilter: 'unread', workflowId: null, projectId: null, search: '[WhatsApp]',
        });
        if (this.generation !== generation || this.tenant.companyId() !== companyId) return;
        items.push(...result.items);
        total = result.totalCount;
        if (result.items.length === 0) break;
        page++;
      } while (items.length < total);
      this.dataSignal.set(items.filter(item => item.authorName === 'WhatsApp' && item.commentPreview.startsWith('[WhatsApp]')));
      this.errorSignal.set(false);
    } catch {
      if (this.generation === generation) this.errorSignal.set(true);
    } finally {
      if (this.activeRequest === generation) this.activeRequest = null;
      if (this.generation === generation) this.loadingSignal.set(false);
    }
  }

  onRead(id: string): void {
    // An older polling response must not restore a reply just marked as read.
    this.generation++;
    this.loadingSignal.set(false);
    this.dataSignal.update(items => items.filter(item => item.id !== id));
  }

  async markAsRead(id: string): Promise<void> {
    const generation = this.generation;
    try {
      await this.api.markAsRead(id);
      if (this.generation === generation) this.onRead(id);
    } catch {
      if (this.generation === generation) this.errorSignal.set(true);
    }
  }
}
